import type { PieceData } from '../../types';
import type { HarmonySpec, Measure, NoteEvent, RegionSpec } from '../../../music/piece';
import { assignLines } from '../../../music/figuration';
import score from './notes.json';

/*
 * J. S. Bach, Prelude in C major, BWV 846 (The Well-Tempered Clavier, Book I).
 *
 * Notes: imported deterministically from data-sources/bwv846/score.mxl by
 * scripts/import_musicxml.py. Harmony: follows Mark Gotham's analysis in the
 * When in Rome corpus (CC BY-SA 4.0), with symbols normalized to this project's
 * notation. Regions, punctuation, paths, moments and what-ifs are this project's
 * editorial layer and are marked as such in the UI.
 */

const C_HOME: RegionSpec[] = [
  { from: 1, to: 5, key: 'C', role: 'home' },
  { from: 6, to: 11, key: 'G', role: 'away' },
  { from: 12, to: 13, key: 'd', role: 'shadow' },
  { from: 14, to: 35, key: 'C', role: 'return' },
];

const harmony: HarmonySpec[] = [
  { bar: 1, key: 'C', rn: 'I' },
  { bar: 2, key: 'C', rn: 'ii42' },
  { bar: 3, key: 'C', rn: 'V65' },
  { bar: 4, key: 'C', rn: 'I' },
  { bar: 5, key: 'C', rn: 'vi6', pivot: { key: 'G', rn: 'ii6' } },
  { bar: 6, key: 'G', rn: 'V42' },
  { bar: 7, key: 'G', rn: 'I6' },
  { bar: 8, key: 'G', rn: 'IV42' },
  { bar: 9, key: 'G', rn: 'ii7' },
  { bar: 10, key: 'G', rn: 'V7' },
  { bar: 11, key: 'G', rn: 'I' },
  { bar: 12, key: 'd', rn: 'viio43' },
  { bar: 13, key: 'd', rn: 'i6' },
  { bar: 14, key: 'C', rn: 'viio43', note: 'A♭ borrowed from C minor' },
  { bar: 15, key: 'C', rn: 'I6' },
  { bar: 16, key: 'C', rn: 'IV42' },
  { bar: 17, key: 'C', rn: 'ii7' },
  { bar: 18, key: 'C', rn: 'V7' },
  { bar: 19, key: 'C', rn: 'I' },
  { bar: 20, key: 'C', rn: 'V7/IV' },
  { bar: 21, key: 'C', rn: 'IV7' },
  { bar: 22, key: 'C', rn: 'viio7/V' },
  { bar: 23, key: 'C', rn: 'viio42', nct: ['C'], note: 'C held over from bar 22, resolving to B in bar 24' },
  { bar: 24, key: 'C', rn: 'V7' },
  { bar: 25, key: 'C', rn: 'Cad64' },
  { bar: 26, key: 'C', rn: 'V7', nct: ['C'], note: 'C held over G: a 4–3 suspension that resolves in bar 27' },
  { bar: 27, key: 'C', rn: 'V7' },
  { bar: 28, key: 'C', rn: 'viio7/V', note: 'over the G pedal' },
  { bar: 29, key: 'C', rn: 'Cad64' },
  { bar: 30, key: 'C', rn: 'V7', nct: ['C'], note: 'C held over G again, resolving in bar 31' },
  { bar: 31, key: 'C', rn: 'V7' },
  { bar: 32, key: 'C', rn: 'V7/IV', note: 'over the tonic pedal' },
  { bar: 33, key: 'C', rn: 'ii42' },
  { bar: 34, key: 'C', rn: 'V65', note: 'over the tonic pedal' },
  { bar: 35, key: 'C', rn: 'I' },
];

const measures: Measure[] = (score.measures as Array<{ bar: number; start: number; length: number }>).map((m) => ({ ...m }));
const notes: NoteEvent[] = assignLines(score.notes as NoteEvent[]);

export const bwv846: PieceData = {
  id: 'bwv846',
  kind: 'keyboard',
  title: 'Prelude in C major',
  catalogue: 'BWV 846',
  collection: 'The Well-Tempered Clavier, Book I',
  composer: 'Johann Sebastian Bach',
  year: '1722',
  homeKey: 'C',
  tempo: 66,
  measures,
  notes,
  harmony,
  pedals: [
    { kind: 'dominant', from: 24, to: 31, pitch: 'G2' },
    { kind: 'tonic', from: 32, to: 35, pitch: 'C2' },
  ],
  regions: C_HOME,
  punctuation: [
    { bar: 11, mark: 'semicolon', label: 'arrival in G', theory: 'Imperfect authentic cadence in G major: V⁷–I with B, the third, on top.' },
    { bar: 19, mark: 'semicolon', label: 'home again', theory: 'Imperfect authentic cadence in C major: ii⁷–V⁷–I with E, the third, on top.' },
    { bar: 24, mark: 'colon', label: 'the wait begins', theory: 'Arrival on the dominant. G stays in the bass for eight bars: a dominant pedal.' },
    { bar: 32, mark: 'semicolon', label: 'home, touched by B♭', theory: 'The pedal resolves to C in the bass, but B♭ turns the tonic into V⁷/IV: arrival without a full stop.' },
    { bar: 35, mark: 'full', label: 'the end', theory: 'Tonic over the tonic pedal with C on top: in this reading, the piece’s only perfect authentic cadence.' },
  ],
  script: [
    { from: 1, to: 4, path: 'home-orbit' },
    { from: 5, to: 11, path: 'pivot-modulation' },
    { from: 12, to: 13, path: 'tonicization' },
    { from: 17, to: 19, path: 'authentic-cadence' },
    { from: 21, to: 23, path: 'converging' },
    { from: 24, to: 31, path: 'ponte' },
    { from: 32, to: 35, path: 'quiescenza' },
  ],
  moments: [
    {
      id: 'orbit',
      from: 1,
      to: 4,
      feeling: 'Leaving home without leaving',
      listenFor: 'The lowest note hardly moves, yet bar 2 leans and bar 3 pulls. Bar 4 lets go.',
      theoryTitle: 'Tonic prolongation: I – ii⁴₂ – V⁶₅ – I',
      theory:
        'The first four bars never leave C major. The bass holds C, dips to B and returns, while the harmony above visits the supertonic and the dominant. Bach sets up the whole gravity field before going anywhere.',
      paths: ['home-orbit'],
      feelings: ['orbit'],
      whatIfs: [
        {
          id: 'still',
          label: 'Stand still',
          question: 'What if the harmony never moved?',
          effect: 'Bars 2 and 3 repeat the tonic chord. Without the lean and the pull, bar 4 has nothing to resolve.',
          ops: [
            { kind: 'revoice', bar: 2, pitches: ['C4', 'E4', 'G4', 'C5', 'E5'] },
            { kind: 'revoice', bar: 3, pitches: ['C4', 'E4', 'G4', 'C5', 'E5'] },
          ],
          harmony: [
            { bar: 2, key: 'C', rn: 'I' },
            { bar: 3, key: 'C', rn: 'I' },
          ],
        },
      ],
    },
    {
      id: 'tilt',
      from: 5,
      to: 11,
      feeling: 'The floor tilts',
      listenFor: 'In bar 6 one note changes, F becomes F♯, and home starts to slide toward G.',
      theoryTitle: 'Modulation to the dominant through a pivot chord',
      theory:
        'Bar 5 (A minor over C) belongs to both keys: vi in C and ii in G. Bar 6 adds F♯, the leading tone of G, and ii⁷–V⁷–I confirms G major in bar 11. The arrival is an imperfect authentic cadence: the top note is B, the third of G.',
      paths: ['pivot-modulation', 'authentic-cadence'],
      feelings: ['tilt'],
      whatIfs: [
        {
          id: 'no-fsharp',
          label: 'Remove the F♯',
          question: 'What if there were no F♯?',
          effect:
            'Every F♯ in bars 6–10 becomes F. The music never leaves C: bar 11 is no longer a new home but a pause on the dominant, a half cadence.',
          ops: [{ kind: 'respell', from: 6, to: 10, pc: 'F#', into: 'F' }],
          harmony: [
            { bar: 6, key: 'C', rn: 'ii42' },
            { bar: 7, key: 'C', rn: 'V6' },
            { bar: 8, key: 'C', rn: 'I42' },
            { bar: 9, key: 'C', rn: 'vi7' },
            { bar: 10, key: 'C', rn: 'ii7' },
            { bar: 11, key: 'C', rn: 'V' },
          ],
          regions: [
            { from: 1, to: 11, key: 'C', role: 'home' },
            { from: 12, to: 13, key: 'd', role: 'shadow' },
            { from: 14, to: 35, key: 'C', role: 'return' },
          ],
          punctuation: [{ bar: 11, mark: 'comma', label: 'a pause on G', theory: 'Half cadence in C major: the phrase stops on the dominant.' }],
        },
      ],
      hold: {
        afterBar: 10,
        prompt: 'The pull toward G is at its strongest. Where should it land?',
        options: [
          { id: 'g', label: 'G major', hint: 'the new home', voicing: ['G3', 'B3', 'D4', 'G4', 'B4'], key: 'G', rn: 'I', composer: true },
          { id: 'em', label: 'E minor', hint: 'a detour', voicing: ['E3', 'B3', 'E4', 'G4', 'B4'], key: 'G', rn: 'vi' },
          { id: 'c', label: 'C major', hint: 'back where we began', voicing: ['C3', 'G3', 'E4', 'G4', 'C5'], key: 'G', rn: 'IV' },
        ],
      },
    },
    {
      id: 'shadow',
      from: 12,
      to: 13,
      feeling: 'A shadow passes',
      listenFor: 'A C♯ darkens bar 12, and bar 13 settles for a moment in D minor.',
      theoryTitle: 'Tonicization of D minor (ii)',
      theory:
        'Bar 12 is the diminished-seventh chord that leads to D minor (vii°⁴₃ of ii); bar 13 is D minor itself. It is a visit, not a move: by bar 14 the diminished seventh of C, with A♭ borrowed from C minor, already pulls home.',
      paths: ['tonicization'],
      feelings: ['shadow'],
      whatIfs: [
        {
          id: 'cut-shadow',
          label: 'Skip the shadow',
          question: 'What if Bach went straight home?',
          effect: 'Bars 12–13 are removed. C major returns sooner, and without the darkening its light registers less.',
          ops: [{ kind: 'cut', from: 12, to: 13 }],
        },
      ],
    },
    {
      id: 'home',
      from: 14,
      to: 19,
      feeling: 'Home, the long way round',
      listenFor: 'The bass walks E, E, D, drops to G, and lands on C in bar 19.',
      theoryTitle: 'Authentic cadence in C: ii⁷ – V⁷ – I',
      theory:
        'After the diminished chord of bar 14, C major returns in first inversion. IV⁴₂, ii⁷ and V⁷ form a standard cadential approach, and bar 19 arrives with E on top: an imperfect authentic cadence, a semicolon rather than a full stop.',
      paths: ['authentic-cadence'],
      feelings: ['home'],
      whatIfs: [
        {
          id: 'deceptive',
          label: 'Let the ground give way',
          question: 'What if the dominant resolved to A minor?',
          effect: 'Bar 19 becomes A minor (vi). The pull of G⁷ is answered with the wrong chord: a deceptive cadence.',
          ops: [{ kind: 'revoice', bar: 19, pitches: ['A2', 'E3', 'A3', 'C4', 'E4'] }],
          harmony: [{ bar: 19, key: 'C', rn: 'vi' }],
          punctuation: [{ bar: 19, mark: 'ellipsis', label: 'the ground gives way', theory: 'Deceptive cadence: V⁷ resolves to vi.' }],
        },
      ],
      hold: {
        afterBar: 18,
        prompt: 'G⁷ is sounding. Where does it want to go?',
        options: [
          { id: 'c', label: 'C major', hint: 'home', voicing: ['C3', 'E3', 'G3', 'C4', 'E4'], key: 'C', rn: 'I', composer: true },
          { id: 'am', label: 'A minor', hint: 'the ground gives way', voicing: ['A2', 'E3', 'A3', 'C4', 'E4'], key: 'C', rn: 'vi' },
          { id: 'f', label: 'F major', hint: 'somewhere softer', voicing: ['F2', 'C3', 'A3', 'C4', 'F4'], key: 'C', rn: 'IV' },
        ],
      },
    },
    {
      id: 'funnel',
      from: 20,
      to: 23,
      feeling: 'Everything funnels in',
      listenFor: 'The bass climbs F, F♯, then jumps to A♭: two chromatic notes squeezing toward G.',
      theoryTitle: 'Chromatic approach to the dominant',
      theory:
        'Bar 20 turns C into a dominant (V⁷/IV, with B♭) and bar 21 answers with IV⁷. Two diminished-seventh chords (bars 22–23) then close in on G from both sides, F♯ below and A♭ above. This is the most chromatic stretch of the piece.',
      paths: ['converging'],
      feelings: ['funnel', 'sweet-pain'],
      whatIfs: [
        {
          id: 'soften',
          label: 'Soften bar 22',
          question: 'What if bar 22 were an ordinary dominant chord?',
          effect: 'E♭ becomes D: the diminished seventh F♯–A–C–E♭ turns into D⁷ (V⁶₅/V). The pull remains; the anguish goes.',
          ops: [{ kind: 'swap', bar: 22, from: 'Eb4', to: 'D4' }],
          harmony: [{ bar: 22, key: 'C', rn: 'V65/V' }],
        },
      ],
    },
    {
      id: 'waiting',
      from: 24,
      to: 31,
      feeling: 'Waiting on the doorstep',
      listenFor: 'The lowest note is G for eight bars. Above it the harmony keeps changing, and each change tightens the wait.',
      theoryTitle: 'Dominant pedal (Ponte)',
      theory:
        'From bar 24 the bass stays on G, the dominant. Over it Bach alternates V⁷ with the cadential ⁶₄, holds C against G in bars 26 and 30, and in bar 28 sounds the diminished seventh of V. Riepel called this kind of held dominant a Ponte, a bridge.',
      paths: ['ponte'],
      feelings: ['waiting', 'sweet-pain'],
      whatIfs: [
        {
          id: 'no-wait',
          label: 'Cut the wait',
          question: 'What if the dominant resolved at once?',
          effect: 'Bars 25–31 are removed. The ending arrives but is not earned: the pedal is what makes arrival feel inevitable.',
          ops: [{ kind: 'cut', from: 25, to: 31 }],
        },
      ],
      hold: {
        afterBar: 31,
        prompt: 'Eight bars of G in the bass. How should home sound?',
        options: [
          { id: 'bach', label: 'C with B♭', hint: 'home, already moving', voicing: ['C2', 'C3', 'G3', 'Bb3', 'E4'], key: 'C', rn: 'V7/IV', composer: true },
          { id: 'plain', label: 'Plain C major', hint: 'home, full stop', voicing: ['C2', 'C3', 'G3', 'C4', 'E4'], key: 'C', rn: 'I' },
          { id: 'am', label: 'A minor', hint: 'the ground gives way', voicing: ['A2', 'E3', 'A3', 'C4', 'E4'], key: 'C', rn: 'vi' },
        ],
      },
    },
    {
      id: 'settling',
      from: 32,
      to: 35,
      feeling: 'Settling after arrival',
      listenFor: 'The bass reaches C and stays. Above it one inner voice sinks B♭–A, then rises B–C.',
      theoryTitle: 'Tonic pedal with a Quiescenza',
      theory:
        'The long dominant resolves to a low C held to the end. Over it the harmony moves V⁷/IV – ii⁴₂ – V⁶₅ – I, tracing the closing gesture Gjerdingen calls the Quiescenza: ♭7–6–♮7–1 over the tonic. The final chord, with C on top, is the only full stop in the piece.',
      paths: ['quiescenza', 'authentic-cadence'],
      feelings: ['settling', 'home'],
      whatIfs: [
        {
          id: 'plain',
          label: 'Arrive plainly',
          question: 'What if bar 32 were a plain C major chord?',
          effect: 'B♭ becomes C. The arrival is clean but flat: the lowered seventh is what makes the ending linger.',
          ops: [{ kind: 'swap', bar: 32, from: 'Bb3', to: 'C4' }],
          harmony: [{ bar: 32, key: 'C', rn: 'I' }],
          punctuation: [{ bar: 32, mark: 'full', label: 'a plain arrival', theory: 'Without B♭ the arrival in bar 32 is a full close.' }],
        },
      ],
    },
  ],
  sources: [
    {
      label: 'Score encoding: When in Rome corpus, WTC I Prelude 1 (score.mxl, commit 1c61fe4)',
      url: 'https://github.com/MarkGotham/When-in-Rome',
      licence: 'Check before commercial use; see PROVENANCE.md',
    },
    {
      label: 'Harmonic analysis: Mark Gotham et al., When in Rome (analysis.txt)',
      url: 'https://github.com/MarkGotham/When-in-Rome',
      licence: 'CC BY-SA 4.0',
    },
  ],
  editorial:
    'Regions, punctuation, paths, moments and what-ifs are this project’s editorial reading, drafted for this prototype and awaiting expert review.',
};
